<template>
  <div class="v-navigation">
    <div class="navigation-header">
      <span class="navigation-title">Навигация</span>
    </div>
    <nav>
      <ul>
        <li
          v-for="section in sections"
          :key="section.id"
          :class="{ active: activeSectionId === section.id }"
          @click="scrollToSection(section.id)"
        >
          <span class="label">{{ section.label }}</span>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style lang="scss" src="./OfferEditDialogNavigation.scss"/>

<script lang="ts">
/** Раздел карточки: id секции в разметке и подпись пункта навигации. */
type NavigationSection = {
  id: string;
  label: string;
};

/** Линия отсчёта: отступ от верха контейнера. Активна последняя секция, чей верх выше линии. */
const ACTIVE_LINE_OFFSET = 15;

/** Допуск на дробный scrollTop — при инерционной прокрутке значения не целые. */
const SCROLL_BOTTOM_THRESHOLD = 2;

/** Пауза без событий scroll, после которой считаем прокрутку доехавшей. */
const SCROLL_SETTLE_DELAY = 150;

export default defineComponent({
  name: 'OfferEditDialogNavigation',

  props: {
    /** Разделы карточки. Список держит сама карточка — там же, где лежат секции. */
    sections: {
      type: Array as PropType<NavigationSection[]>,
      required: true,
    },

    /** Селектор scroll-контейнера, по положению которого считается активный раздел. */
    scrollRootSelector: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      /** id подсвеченного раздела. */
      activeSectionId: null as string | null,

      /** scroll-контейнер. Держим ссылку, чтобы снять обработчик в beforeUnmount. */
      scrollRoot: null as HTMLElement | null,

      /** Пункт выбран кликом — подсветка держится на нём, пока едет прокрутка. */
      pinnedSectionId: null as string | null,

      /** Таймер снятия фиксации. */
      pinTimeoutId: 0,
    };
  },

  mounted() {
    /** Карточка догружает данные и рисует секции (часть под v-if) — ждём отрисовку. */
    this.$nextTick(() => {
      this.setupScrollTracking();
      this.applyTailSpace();
      this.updateActiveSection();
    });

    window.addEventListener('resize', this.onWindowResize);
  },

  beforeUnmount() {
    if (this.scrollRoot === null) {
      // обработчик не вешался — снимать нечего
    } else {
      this.scrollRoot.removeEventListener('scroll', this.updateActiveSection);
    }

    window.removeEventListener('resize', this.onWindowResize);
    window.clearTimeout(this.pinTimeoutId);
  },

  methods: {
    /** Подписывается на прокрутку контейнера карточки. */
    setupScrollTracking(): void {
      const root = document.querySelector(this.scrollRootSelector);

      if (root === null) {
        // scroll-контейнер не отрисован — следить не за чем
      } else {
        this.scrollRoot = root as HTMLElement;
        this.scrollRoot.addEventListener('scroll', this.updateActiveSection, { passive: true });
      }
    },

    /**
     * Хвостовой отступ под последней секцией. Без него ход прокрутки заканчивается
     * раньше, чем последняя секция доедет до линии отсчёта: хвостовые секции делят
     * один и тот же остаток хода и подсветка через них проскакивает.
     * Размер — ровно столько, чтобы верх последней секции дошёл до линии.
     */
    applyTailSpace(): void {
      if (this.scrollRoot === null) {
        // контейнера нет — считать не по чему
      } else {
        const lastSectionId = this.getLastRenderedSectionId();

        if (lastSectionId === null) {
          // секций нет — отступ не нужен
        } else {
          const lastSection = document.getElementById(lastSectionId);
          const tailSpace = this.scrollRoot.clientHeight
            - lastSection.offsetHeight
            - ACTIVE_LINE_OFFSET;

          if (tailSpace > 0) {
            lastSection.style.marginBottom = `${tailSpace}px`;
          } else {
            // последняя секция выше контейнера — доедет до линии сама
            lastSection.style.marginBottom = '';
          }
        }
      }
    },

    /** Высота контейнера изменилась — хвостовой отступ считается от неё. */
    onWindowResize(): void {
      this.applyTailSpace();
      this.updateActiveSection();
    },

    /** Пересчитывает подсветку по текущему положению прокрутки. */
    updateActiveSection(): void {
      if (this.scrollRoot === null) {
        // контейнера нет — считать не по чему
      } else if (this.pinnedSectionId === null) {
        this.activeSectionId = this.calcActiveSectionId();
      } else {
        /** Прокрутка едет к выбранному кликом пункту. Промежуточные положения
         *  подсветку не меняют — ждём остановки. */
        this.restartPinTimeout();
      }
    },

    /** Последняя секция, чей верх выше линии отсчёта. У дна контейнера — последняя секция. */
    calcActiveSectionId(): string | null {
      const distanceToBottom = this.scrollRoot.scrollHeight
        - this.scrollRoot.scrollTop
        - this.scrollRoot.clientHeight;

      let result: string | null = null;

      if (distanceToBottom <= SCROLL_BOTTOM_THRESHOLD) {
        // страховка от дробных значений: у дна активна последняя секция
        result = this.getLastRenderedSectionId();
      } else {
        const activeLine = this.scrollRoot.getBoundingClientRect().top + ACTIVE_LINE_OFFSET;

        for (const section of this.sections) {
          const element = document.getElementById(section.id);

          if (element === null) {
            // секции нет в разметке (например характеристики под v-if) — пропускаем
          } else if (element.getBoundingClientRect().top <= activeLine) {
            result = section.id;
          } else {
            // секция ниже линии отсчёта — активной ещё не стала
          }
        }

        if (result === null) {
          // прокрутка выше первой секции — держим подсветку на ней
          result = this.getFirstRenderedSectionId();
        } else {
          // активная секция найдена
        }
      }

      return result;
    },

    /** Снимает фиксацию через паузу без событий scroll — прокрутка доехала. */
    restartPinTimeout(): void {
      window.clearTimeout(this.pinTimeoutId);

      this.pinTimeoutId = window.setTimeout(() => {
        this.pinnedSectionId = null;
      }, SCROLL_SETTLE_DELAY);
    },

    /** id первой отрисованной секции — часть секций лежит под v-if. */
    getFirstRenderedSectionId(): string | null {
      let result: string | null = null;

      for (const section of this.sections) {
        if (document.getElementById(section.id) === null) {
          // секции нет в разметке — пропускаем
        } else if (result === null) {
          result = section.id;
        } else {
          // первая уже найдена
        }
      }

      return result;
    },

    /** id последней отрисованной секции — часть секций лежит под v-if. */
    getLastRenderedSectionId(): string | null {
      let result: string | null = null;

      for (const section of this.sections) {
        if (document.getElementById(section.id) === null) {
          // секции нет в разметке — пропускаем
        } else {
          result = section.id;
        }
      }

      return result;
    },

    /** Протаскивает scroll-контейнер до секции. */
    scrollToSection(sectionId: string): void {
      const section = document.getElementById(sectionId);

      if (section === null) {
        // секции нет в разметке — протаскивать некуда
      } else {
        /** Пункт выбран руками — подсветка стоит на нём, пока едет прокрутка.
         *  Иначе у дна контейнера она сразу перескочит на последнюю секцию. */
        this.pinnedSectionId = sectionId;
        this.activeSectionId = sectionId;
        this.restartPinTimeout();

        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
});
</script>